// --- SubscriptionConfig thunks (admin establishment scope) ---
export const fetchSubscriptionConfig = createAsyncThunk(
  "subscriptionConfig/fetchSubscriptionConfig",
  async ({ establishment }: { establishment: number }, thunkAPI) => {
    try {
      // GET /api/admin/establishments/{id}/subscriptions/config
      const response = await axiosInstance.get(
        `/admin/establishments/${establishment}/subscriptions/config`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        // Retornar null para representar ausência de configuração
        return null;
      }
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar configuração de assinatura",
        });
      }
    }
  },
);

export const saveSubscriptionConfig = createAsyncThunk(
  "subscriptionConfig/saveSubscriptionConfig",
  async (
    {
      establishment,
      data,
      method = "post",
    }: { establishment: number; data: unknown; method?: "post" | "put" },
    thunkAPI,
  ) => {
    try {
      // POST/PUT /api/admin/establishments/{id}/subscriptions/config
      const url = `/admin/establishments/${establishment}/subscriptions/config`;
      const response = await (method === "put"
        ? axiosInstance.put(url, data)
        : axiosInstance.post(url, data));
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({
          error: error.response?.data || error.message,
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao salvar configuração de assinatura",
        });
      }
    }
  },
);

export const patchSubscriptionBilling = createAsyncThunk(
  "subscriptionConfig/patchSubscriptionBilling",
  async (
    {
      establishment,
      data,
    }: {
      establishment: number;
      data: Partial<{
        free_trial_months: number;
        trial_started_at?: string;
        billing_status?: "trial" | "active" | "past_due" | "inactive";
        last_paid_at?: string;
        next_due_date?: string;
        preferred_charge_source?: "system" | "establishment_balance" | "pix";
      }>;
    },
    thunkAPI,
  ) => {
    try {
      // PATCH /api/admin/establishments/{id}/subscriptions/config/billing
      const response = await axiosInstance.patch(
        `/admin/establishments/${establishment}/subscriptions/config/billing`,
        data,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 404) {
          return thunkAPI.rejectWithValue({
            error: "Configuração de assinatura não encontrada!",
          });
        }
        return thunkAPI.rejectWithValue({
          error: error.response?.data || error.message,
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao atualizar configuração de cobrança",
        });
      }
    }
  },
);

export const patchSubscriptionApproval = createAsyncThunk(
  "subscriptionConfig/patchSubscriptionApproval",
  async (
    {
      establishment,
      action,
    }: {
      establishment: number;
      action: "approve" | "reject";
    },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.patch(
        `/admin/establishments/${establishment}/subscriptions/config/approval/${action}`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 404) {
          return thunkAPI.rejectWithValue({
            error: "Configuração de assinatura não encontrada!",
          });
        }
        return thunkAPI.rejectWithValue({
          error: error.response?.data || error.message,
        });
      }
      return thunkAPI.rejectWithValue({
        error: "Erro desconhecido ao atualizar status da chave PIX",
      });
    }
  },
);
import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { isAxiosError } from "@/utils/axiosInstance";
import { Params } from "@/types";

const endpoint = {
  base: "/subscriptions",
  reportUsage: "/reports/subscription-usage",
  details: "/reports/subscription-details",
};

export const fetchSubscriptions = createAsyncThunk(
  "subscription/fetchSubscriptions",
  async ({ params }: { params: Params }, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`${endpoint.base}`, {
        params,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar assinaturas de cliente",
        });
      }
    }
  },
);

export const fetchSubscriptionsAll = createAsyncThunk(
  "subscription/fetchSubscriptionsAll",
  async (
    params: Omit<Params, "current_page" | "per_page"> & {
      without_pagination: 1;
    },
    thunkAPI,
  ) => {
    try {
      // Fazer requisição direta sem cache - apenas manter na sessão
      const response = await axiosInstance.get(`${endpoint.base}`, {
        params,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar todas as assinaturas",
        });
      }
    }
  },
);

export const createSubscription = createAsyncThunk(
  "subscription/createSubscription",
  async (
    { subscription_id, data }: { subscription_id: string; data: any },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.post(
        `${endpoint.base}/${subscription_id}/clients`,
        data,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao criar cliente da assinatura",
        });
      }
    }
  },
);

export const updateSubscription = createAsyncThunk(
  "subscription/updateSubscription",
  async (
    {
      subscription_id,
      slug,
      data,
    }: { subscription_id: string; slug: string; data: any },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.put(
        `${endpoint.base}/${subscription_id}/clients/${slug}`,
        data,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao atualizar cliente da assinatura",
        });
      }
    }
  },
);

export const deleteSubscription = createAsyncThunk(
  "subscription/deleteSubscription",
  async (
    { subscription_id, slug }: { subscription_id: string; slug: string },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.delete(
        `${endpoint.base}/${subscription_id}/clients/${slug}`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao excluir cliente da assinatura",
        });
      }
    }
  },
);

export const updateSubscriptionUsage = createAsyncThunk(
  "subscription/updateSubscription",
  async (
    {
      subscription_id,
      slug,
      userId,
      dateTicket,
      schedule_Id,
      client_subscription_id,
      data,
    }: {
      subscription_id: string;
      slug: string;
      userId: string;
      dateTicket: string;
      schedule_Id: string;
      client_subscription_id: string;
      data: any;
    },
    thunkAPI,
  ) => {
    try {
      // console.log(subscription_id, slug, data, dateTicket, userId, subscription_id)

      const payload = {
        itens: data,
        userId,
        dateTicket,
        subscription_id,
        schedule_Id,
        client_subscription_id,
      };

      const response = await axiosInstance.post(
        `${endpoint.base}/${subscription_id}/clients/${slug}/usage`,
        payload,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao atualizar cliente da assinatura",
        });
      }
    }
  },
);

export const fetchSubscriptionSummary = createAsyncThunk(
  "subscriptionConfig/fetchSubscriptionSummary",
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(
        `/subscriptions/subscribers/summary`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        // Se não encontrar configuração (404), retornar null ao invés de erro
        return null;
      }
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar configuração de assinatura",
        });
      }
    }
  },
);

export const fetchSubscriptionUsageReport = createAsyncThunk(
  "subscription/fetchSubscriptionUsageReport",
  async ({ params }: { params: any }, thunkAPI) => {
    try {
      const response = await axiosInstance.get(endpoint.reportUsage, {
        params,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar relatório de uso de assinatura",
        });
      }
    }
  },
);

export const fetchSubscriptionDetails = createAsyncThunk(
  "subscription/fetchSubscriptionDetails",
  async ({ params }: { params: any }, thunkAPI) => {
    try {
      const response = await axiosInstance.get(endpoint.details, {
        params,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar detalhes da assinatura",
        });
      }
    }
  },
);

// Manual Payment
export const createManualPayment = createAsyncThunk(
  "subscription/createManualPayment",
  async (
    { subscription_id, data }: { subscription_id: string; data: any },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.post(
        `${endpoint.base}/${subscription_id}/manual-payment`,
        data,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        // If the API returned a JSON body with message, forward it so the slice can show it
        const payload =
          error.response && error.response.data
            ? error.response.data
            : { error: error.message };
        return thunkAPI.rejectWithValue(payload);
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao criar pagamento manual",
        });
      }
    }
  },
);
